import Markdoc, { type Node, type RenderableTreeNode } from '@markdoc/markdoc';
import { grammar } from 'utils/shared/editorialMarkdoc';

export const maxRenderNodes = 50_000;
export const maxRenderedBytes = 1024 * 1024;
const legacyMarkup: Record<string, number> = {
  h1: 128,
  h2: 128,
  h3: 128,
  h4: 128,
  p: 64,
  blockquote: 192,
  ul: 96,
  ol: 96,
  li: 64,
  a: 160,
  strong: 64,
  em: 64,
  u: 64,
  SourceCenter: 64,
  SourceHighlight: 64,
  SourceInternalLink: 160,
};

/** Budget standard HTML and conservative approved-component output together. */
export function assertRenderedBudget(asts: Node[]): number {
  let nodes = 0;
  let renderedBytes = 0;
  function addNodes(count: number) {
    nodes += count;
    if (nodes > maxRenderNodes)
      throw new Error('Editorial body/FAQ exceeds the 50000-node rendering budget');
  }
  // React escapes apostrophes; Markdoc's HTML renderer does not.
  function apostropheBytes(value: RenderableTreeNode): number {
    if (typeof value === 'string') return (value.match(/'/g)?.length || 0) * 5;
    if (!Markdoc.Tag.isTag(value)) return 0;
    return (
      Object.values(value.attributes).reduce<number>(
        (sum, entry) =>
          sum + (typeof entry === 'string' ? (entry.match(/'/g)?.length || 0) * 5 : 0),
        0,
      ) + value.children.reduce<number>((sum, child) => sum + apostropheBytes(child), 0)
    );
  }
  function project(value: RenderableTreeNode): RenderableTreeNode {
    if (!Markdoc.Tag.isTag(value)) return value;
    if (value.name === 'ContentImage') {
      renderedBytes += 8 * 1024;
      const { src, alt, width, height } = value.attributes;
      return new Markdoc.Tag('figure', {}, [new Markdoc.Tag('img', { src, alt, width, height })]);
    }
    if (value.name === 'NewsletterCta') {
      renderedBytes += 16 * 1024;
      return '';
    }
    if (value.name === 'ContentTable') {
      const rows: string[][] = JSON.parse(value.attributes.rows);
      addNodes(5 + rows.length + rows.reduce((sum, row) => sum + row.length, 0));
      // The original resource/lesson/news table components add borders, cell
      // padding and header styles. Budget their markup as well as the cell text.
      renderedBytes +=
        512 + rows.length * 100 + rows.reduce((sum, row) => sum + row.length * 120, 0);
      const [head, ...body] = rows;
      return new Markdoc.Tag('div', { className: 'table-scroll' }, [
        new Markdoc.Tag('table', {}, [
          ...(value.attributes.caption
            ? [new Markdoc.Tag('caption', {}, [value.attributes.caption])]
            : []),
          new Markdoc.Tag('thead', {}, [
            new Markdoc.Tag(
              'tr',
              {},
              head.map((cell) => new Markdoc.Tag('th', { scope: 'col' }, [cell])),
            ),
          ]),
          new Markdoc.Tag(
            'tbody',
            {},
            body.map(
              (row) =>
                new Markdoc.Tag(
                  'tr',
                  {},
                  row.map((cell) => new Markdoc.Tag('td', {}, [cell])),
                ),
            ),
          ),
        ]),
      ]);
    }
    // Use the largest existing Portable Text presentation for shared source
    // validation; styling cannot make a previously checked body exceed the cap.
    renderedBytes += legacyMarkup[value.name] || 0;
    if (
      value.name === 'blockquote' &&
      value.children.length > 1 &&
      value.children.every((child) => Markdoc.Tag.isTag(child) && child.name === 'p')
    ) {
      // The presentation adapter restores each original quote paragraph as its own block.
      renderedBytes += (value.children.length - 1) * legacyMarkup.blockquote;
    }
    return new Markdoc.Tag(value.name, value.attributes, value.children.map(project));
  }
  for (const ast of asts) {
    for (const _node of ast.walk()) {
      addNodes(1);
    }
    const tree = project(Markdoc.transform(ast, grammar));
    const html = Markdoc.renderers.html(tree);
    renderedBytes += new TextEncoder().encode(html).byteLength + apostropheBytes(tree);
    if (renderedBytes > maxRenderedBytes)
      throw new Error('Editorial body/FAQ exceeds the 1MiB rendered response budget');
  }
  return renderedBytes;
}
